import { createTheme } from '@mui/material/styles';

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary:   { main: '#00b4d8', dark: '#0096b7', light: '#33c3e0', contrastText: '#000' },
    secondary: { main: '#7c4dff', dark: '#651fff', light: '#b47cff' },
    error:     { main: '#f44336', dark: '#c62828', light: '#ff7961' },
    warning:   { main: '#ffa726', dark: '#e65100', light: '#ffcc02' },
    success:   { main: '#4caf50', dark: '#2e7d32', light: '#80e27e' },
    info:      { main: '#29b6f6', dark: '#0288d1', light: '#4fc3f7' },
    background: {
      default: '#0a0d14',
      paper:   '#141928',
    },
    text: {
      primary:   '#e8eaf6',
      secondary: '#8892b0',
      disabled:  '#4a5568',
    },
    divider: 'rgba(255,255,255,0.06)',
    custom: {
      sidebar:      '#0f1420',
      appbar:       '#0f1420',
      cardHover:    '#1a2035',
      inputBg:      '#0f1420',
      threatGlow:   'rgba(0,180,216,0.15)',
      highSeverity: '#ff7043',
    },
  },

  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica Neue", sans-serif',
    h1: { fontSize: '2.25rem', fontWeight: 700, letterSpacing: '-0.02em' },
    h2: { fontSize: '1.875rem', fontWeight: 700, letterSpacing: '-0.01em' },
    h3: { fontSize: '1.5rem', fontWeight: 600, letterSpacing: '-0.01em' },
    h4: { fontSize: '1.25rem', fontWeight: 600 },
    h5: { fontSize: '1.1rem', fontWeight: 600 },
    h6: { fontSize: '0.95rem', fontWeight: 600, letterSpacing: '0.02em', textTransform: 'uppercase' },
    subtitle1: { fontSize: '0.9rem', fontWeight: 500 },
    subtitle2: { fontSize: '0.8rem', fontWeight: 500, letterSpacing: '0.06em', textTransform: 'uppercase' },
    body1: { fontSize: '0.9rem', lineHeight: 1.6 },
    body2: { fontSize: '0.8rem', lineHeight: 1.5 },
    caption: { fontSize: '0.72rem', letterSpacing: '0.04em' },
    overline: { fontSize: '0.68rem', letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 600 },
  },

  shape: { borderRadius: 8 },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarColor: '#1a2035 #0a0d14',
          '&::-webkit-scrollbar': { width: 8, height: 8 },
          '&::-webkit-scrollbar-track': { background: '#0a0d14' },
          '&::-webkit-scrollbar-thumb': { background: '#1a2035', borderRadius: 4 },
          '&::-webkit-scrollbar-thumb:hover': { background: '#232d45' },
        },
      },
    },

    MuiAppBar: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { backgroundColor: '#0f1420', borderBottom: '1px solid rgba(255,255,255,0.06)' },
      },
    },

    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#0f1420',
          borderRight: '1px solid rgba(255,255,255,0.06)',
          transition: 'width 200ms cubic-bezier(0.4,0,0.6,1) !important',
          overflowX: 'hidden',
        },
      },
    },

    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: '#141928',
          border: '1px solid rgba(255,255,255,0.06)',
        },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: '#141928',
          border: '1px solid rgba(255,255,255,0.06)',
          transition: 'border-color 200ms ease, box-shadow 200ms ease',
          '&:hover': {
            borderColor: 'rgba(0,180,216,0.3)',
            boxShadow: '0 0 0 1px rgba(0,180,216,0.1), 0 4px 16px rgba(0,0,0,0.5)',
          },
        },
      },
    },

    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 500, letterSpacing: '0.01em', borderRadius: 6 },
        contained: { '&:hover': { filter: 'brightness(1.1)' } },
        outlined: {
          borderColor: 'rgba(255,255,255,0.15)',
          '&:hover': { borderColor: 'rgba(0,180,216,0.6)', backgroundColor: 'rgba(0,180,216,0.06)' },
        },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          transition: 'background-color 150ms ease',
          '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)' },
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 500, fontSize: '0.72rem', letterSpacing: '0.04em', height: 22 },
        filled: {
          '&.MuiChip-colorError':   { backgroundColor: 'rgba(244,67,54,0.15)',  color: '#f44336', border: '1px solid rgba(244,67,54,0.3)' },
          '&.MuiChip-colorWarning': { backgroundColor: 'rgba(255,167,38,0.15)', color: '#ffa726', border: '1px solid rgba(255,167,38,0.3)' },
          '&.MuiChip-colorSuccess': { backgroundColor: 'rgba(76,175,80,0.15)',  color: '#4caf50', border: '1px solid rgba(76,175,80,0.3)' },
          '&.MuiChip-colorInfo':    { backgroundColor: 'rgba(41,182,246,0.15)', color: '#29b6f6', border: '1px solid rgba(41,182,246,0.3)' },
          '&.MuiChip-colorPrimary': { backgroundColor: 'rgba(0,180,216,0.15)',  color: '#00b4d8', border: '1px solid rgba(0,180,216,0.3)' },
        },
      },
    },

    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            backgroundColor: '#0f1420',
            color: '#8892b0',
            fontSize: '0.72rem',
            fontWeight: 600,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            borderBottom: '1px solid rgba(255,255,255,0.08)',
          },
        },
      },
    },

    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: 'background-color 100ms ease',
          '&:hover': { backgroundColor: '#1a2035' },
          '&:last-child td': { borderBottom: 0 },
        },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: { borderBottom: '1px solid rgba(255,255,255,0.04)', fontSize: '0.84rem', padding: '10px 16px' },
      },
    },

    MuiTableContainer: {
      styleOverrides: {
        root: { backgroundImage: 'none', backgroundColor: '#141928' },
      },
    },

    MuiTabs: {
      styleOverrides: {
        root: { borderBottom: '1px solid rgba(255,255,255,0.08)', minHeight: 44 },
        indicator: { height: 2, borderRadius: 2 },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 500, fontSize: '0.84rem', minHeight: 44, padding: '0 16px' },
      },
    },

    MuiTextField: {
      defaultProps: { variant: 'outlined', size: 'small' },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: '#0f1420',
            '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
            '&:hover fieldset': { borderColor: 'rgba(0,180,216,0.4)' },
            '&.Mui-focused fieldset': { borderColor: '#00b4d8' },
          },
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: '#0f1420',
          '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
          '&:hover fieldset': { borderColor: 'rgba(0,180,216,0.4)' },
          '&.Mui-focused fieldset': { borderColor: '#00b4d8' },
        },
      },
    },

    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: 4, height: 6, backgroundColor: 'rgba(255,255,255,0.08)' },
      },
    },

    MuiCircularProgress: {
      defaultProps: { size: 24 },
    },

    MuiDivider: {
      styleOverrides: { root: { borderColor: 'rgba(255,255,255,0.06)' } },
    },

    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          margin: '1px 8px',
          padding: '7px 10px',
          transition: 'background-color 150ms ease, color 150ms ease',
          '&.Mui-selected': {
            backgroundColor: 'rgba(0,180,216,0.12)',
            color: '#00b4d8',
            '& .MuiListItemIcon-root': { color: '#00b4d8' },
            '&:hover': { backgroundColor: 'rgba(0,180,216,0.18)' },
          },
          '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)' },
        },
      },
    },

    MuiListItemIcon: {
      styleOverrides: {
        root: { minWidth: 36, color: '#8892b0', transition: 'color 150ms ease' },
      },
    },

    MuiTooltip: {
      defaultProps: { arrow: true },
      styleOverrides: {
        tooltip: { fontSize: '0.75rem', backgroundColor: '#1a2035', border: '1px solid rgba(255,255,255,0.1)' },
        arrow: { color: '#1a2035' },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: { backgroundImage: 'none', backgroundColor: '#141928', border: '1px solid rgba(255,255,255,0.1)' },
      },
    },

    MuiDialogTitle: {
      styleOverrides: {
        root: { fontSize: '1rem', fontWeight: 600, borderBottom: '1px solid rgba(255,255,255,0.06)', paddingBottom: 16 },
      },
    },

    MuiDialogActions: {
      styleOverrides: {
        root: { borderTop: '1px solid rgba(255,255,255,0.06)', padding: '12px 24px' },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 6, fontSize: '0.84rem' },
        standardError:   { backgroundColor: 'rgba(244,67,54,0.1)',  border: '1px solid rgba(244,67,54,0.25)' },
        standardWarning: { backgroundColor: 'rgba(255,167,38,0.1)', border: '1px solid rgba(255,167,38,0.25)' },
        standardSuccess: { backgroundColor: 'rgba(76,175,80,0.1)',  border: '1px solid rgba(76,175,80,0.25)' },
        standardInfo:    { backgroundColor: 'rgba(41,182,246,0.1)', border: '1px solid rgba(41,182,246,0.25)' },
      },
    },

    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundImage: 'none',
          backgroundColor: '#1a2035',
          border: '1px solid rgba(255,255,255,0.08)',
          minWidth: 160,
          boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
        },
      },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '0.84rem',
          padding: '8px 16px',
          '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)' },
          '&.Mui-selected': { backgroundColor: 'rgba(0,180,216,0.1)' },
        },
      },
    },

    MuiBadge: {
      styleOverrides: {
        badge: { fontSize: '0.65rem', fontWeight: 700, minWidth: 16, height: 16, padding: '0 4px' },
      },
    },

    MuiSkeleton: {
      styleOverrides: {
        root: { backgroundColor: 'rgba(255,255,255,0.06)', transform: 'none', borderRadius: 4 },
      },
    },

    MuiSwitch: {
      styleOverrides: {
        track: { backgroundColor: 'rgba(255,255,255,0.15)' },
      },
    },

    MuiBreadcrumbs: {
      styleOverrides: {
        separator: { color: 'rgba(255,255,255,0.25)' },
        ol: { flexWrap: 'nowrap' },
      },
    },

    MuiFormLabel: {
      styleOverrides: {
        root: { fontSize: '0.84rem', color: '#8892b0' },
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        root: { fontSize: '0.84rem' },
      },
    },

    MuiAccordion: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: '#141928',
          border: '1px solid rgba(255,255,255,0.06)',
          '&:before': { display: 'none' },
          '&.Mui-expanded': { margin: 0 },
        },
      },
    },

    MuiAccordionSummary: {
      styleOverrides: {
        root: { minHeight: 48, '&.Mui-expanded': { minHeight: 48 } },
        content: { '&.Mui-expanded': { margin: '12px 0' } },
      },
    },

    MuiSelect: {
      styleOverrides: {
        select: { backgroundColor: '#0f1420' },
      },
    },

    MuiPopover: {
      styleOverrides: {
        paper: {
          backgroundImage: 'none',
          backgroundColor: '#1a2035',
          border: '1px solid rgba(255,255,255,0.08)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
        },
      },
    },
  },
});

export default theme;
